import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { trunksApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface Trunk {
  id: string
  name: string
  host: string
  port: number
  username: string
  password: string
  codecs: string
  transport: string
  is_active: boolean
  register: boolean
  outbound_proxy: string
  from_domain: string
  from_user: string
}

interface DiagnosticsCheck {
  level: 'ok' | 'warn' | 'error'
  title: string
  message: string
}

interface TrunkDiagnostics {
  checks: DiagnosticsCheck[]
  summary: { ok: number; warn: number; error: number }
  error?: string
}

interface TrunkStatus {
  trunk_id: string
  trunk_name: string
  registered: boolean
  status: string
  expiry: number | null
  server: string
}

interface TrunkLogEntry {
  timestamp: string
  trunk_name: string
  level: 'debug' | 'info' | 'warning' | 'error'
  event_type: string
  message: string
  details: Record<string, unknown>
}

const TRUNK_NAME_REGEX = /^[a-zA-Z0-9äöüÄÖÜéèêàâ ._-]+$/
const SIP_USERNAME_REGEX = /^[a-zA-Z0-9._\-+@]*$/

const emptyForm = {
  name: '',
  host: '',
  port: 5060,
  username: '',
  password: '',
  codecs: 'ulaw,alaw,g722',
  transport: 'udp',
  is_active: true,
  register: true,
  outbound_proxy: '',
  from_domain: '',
  from_user: ''
}

export default function Trunks() {
  const confirm = useConfirm()
  const [trunks, setTrunks] = useState<Trunk[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingTrunk, setEditingTrunk] = useState<Trunk | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [trunkStatuses, setTrunkStatuses] = useState<Record<string, TrunkStatus>>({})
  const [showLogsModal, setShowLogsModal] = useState(false)
  const [selectedTrunkForLogs, setSelectedTrunkForLogs] = useState<Trunk | null>(null)
  const [trunkLogs, setTrunkLogs] = useState<TrunkLogEntry[]>([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [logLevelFilter, setLogLevelFilter] = useState<string>('')
  const [testingRegistration, setTestingRegistration] = useState(false)
  const [diagnostics, setDiagnostics] = useState<TrunkDiagnostics | null>(null)
  const [showDiagnostics, setShowDiagnostics] = useState(false)
  const [diagnosticsTrunkName, setDiagnosticsTrunkName] = useState<string>('')

  useEffect(() => {
    loadTrunks()
  }, [])

  const loadTrunks = async () => {
    try {
      const response = await trunksApi.list()
      const loadedTrunks = response.data.items || []
      setTrunks(loadedTrunks)

      // Load status for each trunk that has registration enabled
      loadedTrunks.forEach((trunk: Trunk) => {
        if (trunk.register) {
          loadTrunkStatus(trunk.id)
        }
      })
    } catch {
      toast.error('Fehler beim Laden der Trunks')
    } finally {
      setLoading(false)
    }
  }

  const loadTrunkStatus = async (trunkId: string) => {
    try {
      const response = await trunksApi.getStatus(trunkId)
      setTrunkStatuses(prev => ({
        ...prev,
        [trunkId]: response.data
      }))
    } catch {
      // Ignore status errors silently
    }
  }

  const openCreateModal = () => {
    setEditingTrunk(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (trunk: Trunk) => {
    setEditingTrunk(trunk)
    setFormData({
      name: trunk.name,
      host: trunk.host,
      port: trunk.port,
      username: trunk.username || '',
      password: '',
      codecs: trunk.codecs || 'ulaw,alaw,g722',
      transport: trunk.transport || 'udp',
      is_active: trunk.is_active,
      register: trunk.register,
      outbound_proxy: trunk.outbound_proxy || '',
      from_domain: trunk.from_domain || '',
      from_user: trunk.from_user || ''
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const submitData: Record<string, unknown> = { ...formData }
      if (!submitData.password) delete submitData.password

      let resp
      if (editingTrunk) {
        resp = await trunksApi.update(editingTrunk.id, submitData)
        toast.success('Trunk aktualisiert')
      } else {
        resp = await trunksApi.create(submitData)
        toast.success('Trunk erstellt')
      }
      closeModal()
      loadTrunks()

      // Show diagnostics from backend (runs after pjsip + dialplan regeneration)
      const diag = resp?.data?.diagnostics as TrunkDiagnostics | undefined
      if (diag && diag.checks && diag.checks.length > 0) {
        setDiagnostics(diag)
        setDiagnosticsTrunkName(resp.data.name || formData.name)
        setShowDiagnostics(true)
        // Brief toast hint if there are errors or warnings
        if (diag.summary.error > 0) toast.error(`${diag.summary.error} Fehler — siehe Diagnose`)
        else if (diag.summary.warn > 0) toast.warning(`${diag.summary.warn} Warnung(en) — siehe Diagnose`)
      }
    } catch {
      toast.error(editingTrunk ? 'Fehler beim Aktualisieren' : 'Fehler beim Erstellen')
    }
  }

  const handleDelete = async (trunk: Trunk) => {
    const confirmed = await confirm({
      title: 'Trunk löschen',
      message: `Trunk "${trunk.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await trunksApi.delete(trunk.id)
      toast.success('Trunk gelöscht')
      loadTrunks()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingTrunk(null)
    setFormData(emptyForm)
  }

  const openLogsModal = async (trunk: Trunk) => {
    setSelectedTrunkForLogs(trunk)
    setShowLogsModal(true)
    setLogLevelFilter('')
    await loadTrunkLogs(trunk.id)
  }

  const loadTrunkLogs = async (trunkId: string, level?: string) => {
    setLogsLoading(true)
    try {
      const response = await trunksApi.getLogs(trunkId, { limit: 200, level: level || undefined })
      setTrunkLogs(response.data.logs || [])
    } catch {
      toast.error('Fehler beim Laden der Logs')
    } finally {
      setLogsLoading(false)
    }
  }

  const handleClearLogs = async () => {
    if (!selectedTrunkForLogs) return
    const confirmed = await confirm({
      title: 'Logs löschen',
      message: `Alle Logs für "${selectedTrunkForLogs.name}" löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await trunksApi.clearLogs(selectedTrunkForLogs.id)
      setTrunkLogs([])
      toast.success('Logs gelöscht')
    } catch {
      toast.error('Fehler beim Löschen der Logs')
    }
  }

  const handleTestRegistration = async (trunk: Trunk) => {
    setTestingRegistration(true)
    try {
      const response = await trunksApi.testRegistration(trunk.id)
      if (response.data.success) {
        toast.success(`Registrierung erfolgreich: ${response.data.status}`)
      } else {
        toast.error(`Registrierung fehlgeschlagen: ${response.data.message}`)
      }
      // Reload status
      loadTrunkStatus(trunk.id)
      // Reload logs if modal is open
      if (showLogsModal && selectedTrunkForLogs?.id === trunk.id) {
        await loadTrunkLogs(trunk.id, logLevelFilter)
      }
    } catch {
      toast.error('Fehler beim Testen der Registrierung')
    } finally {
      setTestingRegistration(false)
    }
  }

  const formatLogTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'error': return <Badge bg="danger">Fehler</Badge>
      case 'warning': return <Badge bg="warning" text="dark">Warnung</Badge>
      case 'info': return <Badge bg="info">Info</Badge>
      case 'debug': return <Badge bg="secondary">Debug</Badge>
      default: return <Badge bg="secondary">{level}</Badge>
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>SIP Trunks</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neuer Trunk
        </Button>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Host</th>
              <th>Port</th>
              <th>Benutzer</th>
              <th>Aktiv</th>
              <th>Registrierung</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-4">Laden...</td></tr>
            ) : trunks.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-4 text-muted">Keine Trunks konfiguriert</td></tr>
            ) : (
              trunks.map(trunk => {
                const status = trunkStatuses[trunk.id]
                return (
                  <tr key={trunk.id}>
                    <td><strong>{trunk.name}</strong></td>
                    <td>{trunk.host}</td>
                    <td>{trunk.port}</td>
                    <td>{trunk.username || '-'}</td>
                    <td><Badge bg={trunk.is_active ? 'success' : 'secondary'}>{trunk.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                    <td>
                      {trunk.register ? (
                        status ? (
                          <span title={status.expiry ? `Erneuert in ${Math.floor(status.expiry / 60)} Min` : ''}>
                            {status.registered ? (
                              <Badge bg="success"><i className="bi bi-check-circle me-1"></i>Registriert</Badge>
                            ) : (
                              <Badge bg="danger"><i className="bi bi-x-circle me-1"></i>{status.status}</Badge>
                            )}
                          </span>
                        ) : (
                          <Badge bg="secondary"><i className="bi bi-hourglass-split me-1"></i>Prüfe...</Badge>
                        )
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-1"
                        onClick={() => openLogsModal(trunk)}
                        title="Logs anzeigen"
                      >
                        <i className="bi bi-journal-text"></i>
                      </Button>
                      {trunk.register && (
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-1"
                          onClick={() => handleTestRegistration(trunk)}
                          disabled={testingRegistration}
                          title="Registrierung testen"
                        >
                          <i className="bi bi-arrow-repeat"></i>
                        </Button>
                      )}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-1"
                        onClick={async () => {
                          try {
                            const r = await trunksApi.diagnostics(trunk.id)
                            setDiagnostics(r.data as TrunkDiagnostics)
                            setDiagnosticsTrunkName(trunk.name)
                            setShowDiagnostics(true)
                          } catch {
                            toast.error('Diagnose fehlgeschlagen')
                          }
                        }}
                        title="Diagnose durchführen"
                      >
                        <i className="bi bi-clipboard-check"></i>
                      </Button>
                      <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(trunk)}>
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(trunk)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingTrunk ? 'Trunk bearbeiten' : 'Neuer Trunk'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="z.B. Telekom-Trunk"
                    isInvalid={!!formData.name && !TRUNK_NAME_REGEX.test(formData.name)}
                  />
                  <Form.Control.Feedback type="invalid">
                    Keine Sonderzeichen erlaubt (nur Buchstaben, Zahlen, Leerzeichen, . _ -)
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Host *</Form.Label>
                  <Form.Control
                    value={formData.host}
                    onChange={(e) => setFormData({...formData, host: e.target.value})}
                    required
                    placeholder="z.B. sip.provider.de"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Port</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.port}
                    onChange={(e) => setFormData({...formData, port: parseInt(e.target.value) || 5060})}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Transport</Form.Label>
                  <Form.Select
                    value={formData.transport}
                    onChange={(e) => setFormData({...formData, transport: e.target.value})}
                  >
                    <option value="udp">UDP</option>
                    <option value="tcp">TCP</option>
                    <option value="tls">TLS</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Benutzername</Form.Label>
                  <Form.Control
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    placeholder="SIP-Benutzername"
                    isInvalid={!!formData.username && !SIP_USERNAME_REGEX.test(formData.username)}
                  />
                  <Form.Control.Feedback type="invalid">
                    Keine Leerzeichen oder Sonderzeichen erlaubt (nur Buchstaben, Zahlen, . _ - + @)
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{editingTrunk ? 'Neues Passwort (leer lassen)' : 'Passwort'}</Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="SIP-Passwort"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Codecs</Form.Label>
              <Form.Control
                value={formData.codecs}
                onChange={(e) => setFormData({...formData, codecs: e.target.value})}
                placeholder="ulaw,alaw,g722,g729"
              />
              <Form.Text className="text-muted">Komma-getrennte Liste der Codecs</Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>From Domain</Form.Label>
                  <Form.Control
                    value={formData.from_domain}
                    onChange={(e) => setFormData({...formData, from_domain: e.target.value})}
                    placeholder="Optional"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>From User</Form.Label>
                  <Form.Control
                    value={formData.from_user}
                    onChange={(e) => setFormData({...formData, from_user: e.target.value})}
                    placeholder="Optional"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Outbound Proxy</Form.Label>
              <Form.Control
                value={formData.outbound_proxy}
                onChange={(e) => setFormData({...formData, outbound_proxy: e.target.value})}
                placeholder="Optional - z.B. proxy.provider.de"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="register"
                    label="Registrierung beim Provider"
                    checked={formData.register}
                    onChange={(e) => setFormData({...formData, register: e.target.checked})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="is_active"
                    label="Aktiv"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Abbrechen</Button>
            <Button
              variant="primary"
              type="submit"
              disabled={
                (!!formData.name && !TRUNK_NAME_REGEX.test(formData.name)) ||
                (!!formData.username && !SIP_USERNAME_REGEX.test(formData.username))
              }
            >
              {editingTrunk ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Logs Modal */}
      <Modal show={showLogsModal} onHide={() => setShowLogsModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-journal-text me-2"></i>
            Trunk-Logs: {selectedTrunkForLogs?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-2">
              <Form.Select
                size="sm"
                style={{ width: '150px' }}
                value={logLevelFilter}
                onChange={(e) => {
                  setLogLevelFilter(e.target.value)
                  if (selectedTrunkForLogs) {
                    loadTrunkLogs(selectedTrunkForLogs.id, e.target.value)
                  }
                }}
              >
                <option value="">Alle Level</option>
                <option value="error">Fehler</option>
                <option value="warning">Warnungen</option>
                <option value="info">Info</option>
                <option value="debug">Debug</option>
              </Form.Select>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => selectedTrunkForLogs && loadTrunkLogs(selectedTrunkForLogs.id, logLevelFilter)}
              >
                <i className="bi bi-arrow-clockwise"></i>
              </Button>
            </div>
            <div className="d-flex gap-2">
              {selectedTrunkForLogs?.register && (
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => selectedTrunkForLogs && handleTestRegistration(selectedTrunkForLogs)}
                  disabled={testingRegistration}
                >
                  <i className="bi bi-arrow-repeat me-1"></i>
                  Registrierung testen
                </Button>
              )}
              <Button variant="outline-danger" size="sm" onClick={handleClearLogs}>
                <i className="bi bi-trash me-1"></i>
                Logs löschen
              </Button>
            </div>
          </div>

          {logsLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              Lade Logs...
            </div>
          ) : trunkLogs.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bi bi-journal-x d-block fs-1 mb-2"></i>
              Keine Logs vorhanden
            </div>
          ) : (
            <Table hover size="sm" className="mb-0">
              <thead className="sticky-top bg-white">
                <tr>
                  <th style={{ width: '140px' }}>Zeit</th>
                  <th style={{ width: '80px' }}>Level</th>
                  <th style={{ width: '120px' }}>Typ</th>
                  <th>Nachricht</th>
                </tr>
              </thead>
              <tbody>
                {trunkLogs.map((log, idx) => (
                  <tr key={idx} className={log.level === 'error' ? 'table-danger' : log.level === 'warning' ? 'table-warning' : ''}>
                    <td className="text-nowrap small">{formatLogTime(log.timestamp)}</td>
                    <td>{getLevelBadge(log.level)}</td>
                    <td className="small">{log.event_type}</td>
                    <td>
                      <span>{log.message}</span>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <small className="text-muted d-block">
                          {Object.entries(log.details)
                            .filter(([, v]) => v)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(', ')}
                        </small>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <small className="text-muted me-auto">
            {trunkLogs.length} Log-Einträge
          </small>
          <Button variant="secondary" onClick={() => setShowLogsModal(false)}>
            Schliessen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Trunk Diagnostics Modal — shown after create/update and when opened manually */}
      <Modal show={showDiagnostics} onHide={() => setShowDiagnostics(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-clipboard-check me-2"></i>
            Trunk-Diagnose{diagnosticsTrunkName && ': ' + diagnosticsTrunkName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {diagnostics && (
            <>
              <div className="d-flex gap-2 mb-3">
                <Badge bg="success">{diagnostics.summary.ok} OK</Badge>
                {diagnostics.summary.warn > 0 && <Badge bg="warning" text="dark">{diagnostics.summary.warn} Warnung(en)</Badge>}
                {diagnostics.summary.error > 0 && <Badge bg="danger">{diagnostics.summary.error} Fehler</Badge>}
              </div>
              <Table size="sm" className="mb-0">
                <tbody>
                  {diagnostics.checks.map((c, i) => (
                    <tr key={i}>
                      <td style={{ width: '40px' }} className="text-center align-middle">
                        {c.level === 'ok' && <i className="bi bi-check-circle-fill text-success"></i>}
                        {c.level === 'warn' && <i className="bi bi-exclamation-triangle-fill text-warning"></i>}
                        {c.level === 'error' && <i className="bi bi-x-circle-fill text-danger"></i>}
                      </td>
                      <td style={{ width: '160px' }} className="align-middle"><strong>{c.title}</strong></td>
                      <td className="align-middle">{c.message}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {diagnostics.error && (
                <div className="alert alert-danger mt-3 mb-0 small">
                  Diagnose-Fehler: {diagnostics.error}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDiagnostics(false)}>Schliessen</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
